import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SignaturePad } from '../components/SignaturePad.jsx';
import { api } from '../api.js';
import { Centered } from './NotFound.jsx';

function uuid() { return crypto.randomUUID(); }

export function SignerSign() {
  const { signerToken } = useParams();
  const [state, setState] = useState({ loading: true });
  const [form, setForm] = useState({});
  const [signature, setSignature] = useState(null);
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.getSigner(signerToken)
      .then((d) => { setState({ loading: false, data: d }); setForm(d.owner || {}); })
      .catch((e) => setState({ loading: false, error: e }));
  }, [signerToken]);

  if (state.loading) return <Centered title="Loading…"><p>Preparing your signature page.</p></Centered>;
  if (state.error?.status === 409) return <Centered title="Not your turn yet"><p>The previous owner hasn't signed yet. You'll get a fresh link when it's your turn.</p></Centered>;
  if (state.error?.status === 410) return <Centered title="This link is no longer active"><p>Please contact your Fundmerica representative.</p></Centered>;
  if (state.error) return <Centered title="Link not found"><p>This signing link could not be opened.</p></Centered>;
  if (state.data?.already_signed || done) return <Centered title="Thank you"><p>Your signature has been recorded.</p></Centered>;

  // An owner signature without SSN/DOB/address is unusable for underwriting —
  // block signing until every required field is present (matches the API's check).
  const hasSsnOnFile = !!state.data?.owner?.ssn_last4;
  const ssnDigits = (form.ssn || '').replace(/\D/g, '');
  const missing = [];
  if (!(form.first_name || '').trim()) missing.push('First name');
  if (!(form.last_name || '').trim()) missing.push('Last name');
  if (!(form.address_line1 || '').trim()) missing.push('Street');
  if (!(form.city || '').trim()) missing.push('City');
  if (!(form.state || '').trim()) missing.push('State');
  if (!(form.zip || '').trim()) missing.push('ZIP');
  if ((form.ssn || '').trim() !== '' && ssnDigits.length !== 9) missing.push('SSN (9 digits)');
  else if (!hasSsnOnFile && ssnDigits.length !== 9) missing.push('SSN');
  if (!(form.dob || '').trim()) missing.push('Date of birth');
  else {
    // 5-digit years slip through type=date; require YYYY-MM-DD and age 18–110.
    const age = (Date.now() - new Date(form.dob).getTime()) / (365.25 * 24 * 3600 * 1000);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dob) || isNaN(age) || age < 18 || age > 110) missing.push('Date of birth (valid date, 18+)');
  }
  // Ownership %: the API requires a number 0–100. Typing "50%" used to become NaN →
  // JSON null → a cryptic 400 ("Expected number, received null"); 150 → "must be <= 100".
  // Parse leniently (strip % and spaces) and validate here so the merchant sees why.
  const pctRaw = String(form.ownership_pct ?? '').trim();
  const pctNum = pctRaw === '' ? null : Number(pctRaw.replace(/[^0-9.]/g, ''));
  if (pctRaw === '' || pctNum === null || Number.isNaN(pctNum) || pctNum <= 0 || pctNum > 100) missing.push('Ownership % (1–100)');
  const canSign = !!signature && c1 && c2 && missing.length === 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit() {
    setErr(null); if (!canSign) return; setBusy(true);
    try {
      await api.signAsOwner(signerToken, {
        first_name: form.first_name, last_name: form.last_name,
        ownership_pct: pctNum,
        email: form.email, phone: form.phone,
        address_line1: form.address_line1, address_line2: form.address_line2,
        city: form.city, state: form.state, zip: form.zip,
        ssn: form.ssn || undefined, dob: form.dob || undefined,
        signature_svg: signature.svg, signature_png_b64: signature.png_b64,
        consent_esign: true, consent_credit: true, idempotency_key: uuid(),
      });
      setDone(true);
    } catch (e) {
      // Turn "ownership_pct: Number must be less than or equal to 100" into something a merchant can act on.
      const raw = e.body?.message || e.message || '';
      const friendly = e.body?.error === 'validation_error'
        ? 'Please check: ' + raw.replace(/^ownership_pct:/, 'Ownership %:').replace(/^([a-z0-9_]+):/, (m, k) => k.replace(/_/g, ' ') + ':')
        : raw;
      setErr(friendly);
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <header>
          <h2 className="text-xl font-semibold text-brand-900">Confirm your information & sign</h2>
          <p className="text-sm text-slate-500">{state.data.package.business_name} — please verify your details and sign.</p>
        </header>

        {state.data?.business && Object.keys(state.data.business).length > 0 && (
          <div className="card p-4">
            <h3 className="font-semibold mb-2">Business information</h3>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(state.data.business).map(([k, v]) => v ? (
                <div key={k}>
                  <dt className="text-slate-500">{k.replace(/_/g, ' ')}</dt>
                  <dd>{String(v).replace(/^"|"$/g, '')}</dd>
                </div>
              ) : null)}
            </dl>
          </div>
        )}

        <div className="card p-4 grid grid-cols-2 gap-3">
          <input className="field-input" placeholder="First name *" value={form.first_name || ''} onChange={set('first_name')} />
          <input className="field-input" placeholder="Last name *" value={form.last_name || ''} onChange={set('last_name')} />
          <input className="field-input" placeholder="Email" value={form.email || ''} onChange={set('email')} />
          <input className="field-input" placeholder="Phone" value={form.phone || ''} onChange={set('phone')} />
          <input className="field-input" type="number" inputMode="decimal" min="1" max="100" step="any" placeholder="Ownership % *" value={form.ownership_pct ?? ''} onChange={set('ownership_pct')} />
          <input className="field-input" placeholder="Street *" value={form.address_line1 || ''} onChange={set('address_line1')} />
          <input className="field-input" placeholder="City *" value={form.city || ''} onChange={set('city')} />
          <input className="field-input" placeholder="State *" value={form.state || ''} onChange={set('state')} />
          <input className="field-input" placeholder="ZIP *" value={form.zip || ''} onChange={set('zip')} />
          <input className="field-input" placeholder={hasSsnOnFile ? `SSN (on file ending ${state.data.owner.ssn_last4})` : 'SSN *'} value={form.ssn || ''} onChange={set('ssn')} />
          <label className="flex flex-col text-xs text-slate-500 col-span-2">Date of birth *
            <input type="date" min="1900-01-01" max={new Date().toISOString().slice(0, 10)} className="field-input" value={form.dob || ''} onChange={set('dob')} />
          </label>
          {missing.length > 0 && (
            <div className="col-span-2 text-xs text-amber-700">Required before signing: {missing.join(', ')}</div>
          )}
        </div>

        <div className="card p-4"><SignaturePad onChange={setSignature} /></div>

        <div className="card p-4 space-y-3 text-sm">
          <label className="flex items-start gap-3"><input type="checkbox" className="mt-1" checked={c1} onChange={(e) => setC1(e.target.checked)} /><span>I agree to sign electronically under the E-SIGN Act.</span></label>
          <label className="flex items-start gap-3"><input type="checkbox" className="mt-1" checked={c2} onChange={(e) => setC2(e.target.checked)} /><span>I authorize Fundmerica and its funding partners to obtain consumer and business credit reports.</span></label>
        </div>

        {err && <div className="field-error">{err}</div>}
        <div className="flex justify-end">
          <button className="btn-primary" onClick={submit} disabled={!canSign || busy}>{busy ? 'Submitting…' : 'Sign and submit'}</button>
        </div>
      </div>
    </div>
  );
}
